/**
 * stabilityGate — okénko stability pro auto-záchyt hodnoty z kvantizovaného DUT.
 *
 * Akceptuje hodnotu jen když:
 *   - status === Weight Stable (4),
 *   - jednotka === gram (jinak `unitError` — tvrdá blokace, NEpřepočítáváme),
 *   - posledních N čtení leží v rozsahu ≤ ±1 LSB (rozlišení) → výsledek = MEDIÁN okna.
 * Hlásí i fault / overweight / in_motion / timeout. Čistá logika (bez DOM) → testovatelná.
 *
 * Použití:
 *   const gate = createStabilityGate({ n: 5, lsb: 1, timeoutMs: 15000 });
 *   gate.on('accepted', ({ value }) => { ... });
 *   gate.on('overweight', () => { ... }); // atd.
 *   gate.arm();          // začni sledovat (před výzvou "Položte na X g")
 *   gate.feed(reading);  // krmit příchozími readingy z provideru
 *   gate.disarm();       // přestaň sledovat
 */
import { STATUS, UNIT } from './dymoParser';

function median(arr) {
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function createStabilityGate(opts = {}) {
  const { n = 5, lsb = 1, timeoutMs = 15000 } = opts;
  const handlers = {};
  let win = [];
  let armed = false;
  let timeoutId = null;

  function emit(event, payload) {
    if (handlers[event]) handlers[event](payload);
  }

  function clearTimer() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }

  function reset() {
    win = [];
  }

  const api = {
    on(event, cb) { handlers[event] = cb; return api; },

    arm() {
      armed = true;
      reset();
      clearTimer();
      if (timeoutMs > 0 && typeof setTimeout === 'function') {
        timeoutId = setTimeout(() => {
          if (armed) {
            armed = false;
            reset();
            emit('timeout');
          }
        }, timeoutMs);
      }
      return api;
    },

    disarm() {
      armed = false;
      reset();
      clearTimer();
      return api;
    },

    feed(reading) {
      if (!armed || !reading) return;
      const { status, unit, grams } = reading;

      // Chybové stavy — zastav a ohlas.
      if (status === STATUS.FAULT || status === STATUS.NEEDS_CALIBRATION || status === STATUS.NEEDS_REZEROING) {
        reset();
        emit('fault', reading);
        return;
      }
      if (status === STATUS.OVER_WEIGHT) {
        reset();
        emit('overweight', reading);
        return;
      }
      // Tvrdý jednotkový guard — metodika je v gramech, NEpřepočítáváme z oz/lb.
      if (unit !== UNIT.GRAM) {
        reset();
        emit('unitError', reading);
        return;
      }
      // Pohyb — okno resetovat, čekat na ustálení.
      if (status === STATUS.IN_MOTION) {
        reset();
        emit('inMotion', reading);
        return;
      }
      // Akceptujeme jen Weight Stable (ne Stable-at-Zero — to není naměřený bod).
      if (status !== STATUS.STABLE || grams == null) {
        reset();
        return;
      }

      win.push(grams);
      if (win.length > n) win.shift();
      emit('reading', { value: grams, reading });

      if (win.length === n && (Math.max(...win) - Math.min(...win)) <= lsb) {
        const value = median(win);
        api.disarm();
        emit('accepted', { value, reading });
      }
    },
  };

  return api;
}
